import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  deleteSlackUserToken,
  getSlackConnection,
  setSlackUserToken,
  UserKeys,
} from '../../../../api/users-api';
import { isFeatureEnabled } from '../../../../utils/feature-flags';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import { handleErrors } from '../../../../utils/errors';
import { Messages } from '../../../../utils/message-constants';
import {
  AdministrationRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import { useUserSettings } from '../../../../utils/use-user-settings';
import { useStore } from '../../../state/zustand-store';
import SlackLogo from '../../../../assets/slack-logo.svg';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import IntegrationCard from './integration-card';
import SlackConnectionCard from './slack-connection-card';
import SlackTokenForm from './slack-token-form';
import { useNavigate } from 'react-router-dom';
import { pagesConfig } from '../../../../shared/pages-config';
import type { SxProps, Theme } from '@mui/material/styles';

const SLACK_CONNECT_DESCRIPTION =
  'Enhance team communication by integrating with Google Drive, Calendar and Meet.';
const SLACK_USER_TOKEN_PREFIX = 'xoxp-';

export interface SlackIntegrationCardProps {
  sx?: SxProps<Theme>;
}

export default function SlackIntegrationCard({
  sx,
}: SlackIntegrationCardProps) {
  const navigate = useNavigate();
  const { features, administrationRoles, changeLogRoles } = useUserSettings();
  const setSnackbar = useStore((state) => state.setSnackbar);
  const { createEvent } = useAnalytics();
  const queryClient = useQueryClient();

  const hasUpdatePermission = isAbleToSeeUIElement(
    administrationRoles,
    AdministrationRoles.UpdateSlackIntegration.value
  );
  const requiresChangeLog = isAbleToSeeUIElement(
    changeLogRoles,
    AdministrationRoles.UpdateSlackIntegration.value
  );

  const { data: connection, isLoading: isConnectionLoading } = useQuery({
    queryKey: [UserKeys.SlackConnection],
    queryFn: () => getSlackConnection(),
    enabled: isFeatureEnabled('slackIntegration', features),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('Slack connection'),
    },
  });

  const [userToken, setUserToken] = useState('');
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [isConnectingOpen, setIsConnectingOpen] = useState(false);
  const [connectionProgress, setConnectionProgress] = useState<
    number | undefined
  >(undefined);
  const pendingConnectedRef = useRef(false);
  const [localConnectionOverride, setLocalConnectionOverride] = useState<
    boolean | null
  >(null);
  const [changeLogAction, setChangeLogAction] = useState<
    'connect' | 'disconnect'
  >('connect');
  const [prevConnectionConnected, setPrevConnectionConnected] = useState(
    connection?.connected
  );

  if (prevConnectionConnected !== connection?.connected) {
    setPrevConnectionConnected(connection?.connected);
    setLocalConnectionOverride(null);
  }
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const snackbarTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
      if (snackbarTimerRef.current) {
        clearTimeout(snackbarTimerRef.current);
      }
    };
  }, []);

  const trimmedToken = userToken.trim();
  const hasInvalidPrefix =
    trimmedToken.length > 0 &&
    !trimmedToken.startsWith(SLACK_USER_TOKEN_PREFIX);

  const handleCloseConnectingDialog = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
    }
    if (pendingConnectedRef.current) {
      setLocalConnectionOverride(true);
      pendingConnectedRef.current = false;
    }
    setConnectionProgress(undefined);
    setIsConnectingOpen(false);
  };

  const {
    mutate: saveToken,
    isPending: isSaving,
    isError: isSaveError,
    reset: resetSaveToken,
  } = useMutation({
    mutationFn: () => setSlackUserToken(userToken.trim(), changeLogMessage),
    onSuccess: () => {
      pendingConnectedRef.current = true;

      setUserToken('');
      setChangeLogMessage('');
      queryClient.invalidateQueries({ queryKey: [UserKeys.SlackConnection] });

      setConnectionProgress(100);

      closeTimerRef.current = setTimeout(() => {
        handleCloseConnectingDialog();
      }, 800);

      snackbarTimerRef.current = setTimeout(() => {
        setSnackbar({
          open: true,
          severity: 'success',
          message: Messages.Success.Updated('Slack user token'),
        });
      }, 800);
    },
    onError: (saveError) => {
      if (snackbarTimerRef.current) {
        clearTimeout(snackbarTimerRef.current);
      }
      pendingConnectedRef.current = false;
      setLocalConnectionOverride(null);
      handleCloseConnectingDialog();
      handleErrors({
        error: saveError,
        snackbarMessage: Messages.Errors.Network.Updating('Slack user token'),
      });
    },
    onSettled: () => {
      createEvent(
        analytics.administration.settings.add_slack_integration_token,
        {
          module: ModuleList.modules.administration.name,
          section: ModuleList.modules.administration.sections.settings,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });

  function startConnecting() {
    setOpenChangeLogDialog(false);
    pendingConnectedRef.current = false;
    setLocalConnectionOverride(false);
    setIsConnectingOpen(true);
    setConnectionProgress(20);
    saveToken();
  }

  const { mutate: disconnect, isPending: isDisconnecting } = useMutation({
    mutationFn: () => deleteSlackUserToken(changeLogMessage),
    onSuccess: () => {
      setLocalConnectionOverride(false);
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Disconnected('Slack connection'),
      });
      setChangeLogMessage('');
      queryClient.invalidateQueries({ queryKey: [UserKeys.SlackConnection] });
    },
    onError: (deleteError) => {
      setLocalConnectionOverride(null);
      handleErrors({
        error: deleteError,
        snackbarMessage: Messages.Errors.Network.Deleting('Slack connection'),
      });
    },
    onSettled: () => {
      createEvent(
        analytics.administration.settings.remove_slack_integration_token,
        {
          module: ModuleList.modules.administration.name,
          section: ModuleList.modules.administration.sections.settings,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });

  function startDisconnecting() {
    setOpenChangeLogDialog(false);
    setLocalConnectionOverride(false);
    disconnect();
  }

  function handleDisconnect() {
    if (requiresChangeLog) {
      setChangeLogAction('disconnect');
      setOpenChangeLogDialog(true);
    } else {
      startDisconnecting();
    }
  }

  function handleSubmit() {
    if (!hasUpdatePermission || trimmedToken.length === 0 || hasInvalidPrefix) {
      return;
    }

    if (requiresChangeLog) {
      setChangeLogAction('connect');
      setOpenChangeLogDialog(true);
    } else {
      startConnecting();
    }
  }

  const isConnected =
    localConnectionOverride !== null
      ? localConnectionOverride
      : connection?.connected === true;

  const handleUserTokenChange = (value: string) => {
    setUserToken(value);
    if (isSaveError) {
      resetSaveToken();
    }
  };

  const content = isConnected ? (
    <SlackConnectionCard connection={connection} />
  ) : (
    <SlackTokenForm
      userToken={userToken}
      setUserToken={handleUserTokenChange}
      hasError={isSaveError}
      onSubmit={handleSubmit}
    />
  );

  return (
    <>
      <IntegrationCard
        logo={SlackLogo}
        title="Slack"
        description={SLACK_CONNECT_DESCRIPTION}
        content={content}
        isLoading={isConnectionLoading}
        isConnected={isConnected}
        secondaryButtonLabel="Manage Slack Channels"
        onSecondaryButtonClick={() =>
          navigate(pagesConfig.manageSlackChannels.link)
        }
        primaryButtonDisabled={
          !hasUpdatePermission ||
          (!isConnected && (trimmedToken.length === 0 || hasInvalidPrefix))
        }
        primaryButtonLoading={isSaving || isDisconnecting}
        primaryButtonTooltip={
          !hasUpdatePermission
            ? Messages.Errors.Permission.Acting(
                AdministrationRoles.UpdateSlackIntegration.name
              )
            : ''
        }
        onPrimaryButtonClick={handleSubmit}
        onDisconnectConfirm={handleDisconnect}
        skipDisconnectConfirmDialog={requiresChangeLog}
        isConnectingDialogOpen={isConnectingOpen}
        onConnectingDialogClose={handleCloseConnectingDialog}
        connectingProgress={connectionProgress}
        sx={sx}
      />
      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={
          changeLogAction === 'disconnect'
            ? startDisconnecting
            : startConnecting
        }
        mutateLoading={isSaving || isDisconnecting}
      />
    </>
  );
}
